/**
 * Attribute a gap in the main thread's 1s diagnostics poll (#1500).
 *
 * A stalled screen that comes back with a huge painted dwell has two very different explanations,
 * and they need opposite responses:
 *
 * - `process_suspend` — the whole WebView froze (Android doze, display off, backgrounded). The
 *   player was never stuck; dwell accrued while nothing ran, so faulting on it is a false positive.
 * - `main_thread_block` — only the UI thread stopped (long task, GC, decoder wedge) while the
 *   playback worker kept beating. That *is* a real stall and must still fault.
 *
 * The worker thread is the witness: it records gaps in its own beat, and the main thread reads that
 * only after detecting a gap of its own.
 */

export type TMainLoopGapCause = 'process_suspend' | 'main_thread_block' | 'unknown'

/** How close the worker's gap must land to the main gap's end to be considered the same freeze. */
export const MAIN_LOOP_GAP_MATCH_WINDOW_MS = 3_000

/** Worker gap must cover at least this share of the main gap to count as "both threads froze". */
const MAIN_LOOP_GAP_COVERAGE = 0.5

export function classifyMainLoopGap(input: {
  /** Wall ms the main-thread poll was absent. */
  gapMs: number
  /** Wall ms when the main-thread poll resumed. */
  gapEndedAtMs: number
  /** Worker's own last observed beat gap; 0 when it never missed one. */
  workerLastGapMs: number
  workerLastGapAtMs: number
}): TMainLoopGapCause {
  if (!(input.gapMs > 0)) {
    return 'unknown'
  }
  const workerSawGap = input.workerLastGapAtMs > 0
    && Math.abs(input.gapEndedAtMs - input.workerLastGapAtMs) <= MAIN_LOOP_GAP_MATCH_WINDOW_MS
  if (!workerSawGap) {
    /**
     * Worker kept beating through the window — only the UI thread stopped. Reported as-is so a real
     * main-thread wedge is never laundered into "the device was asleep".
     */
    return 'main_thread_block'
  }
  if (input.workerLastGapMs >= input.gapMs * MAIN_LOOP_GAP_COVERAGE) {
    return 'process_suspend'
  }
  // Worker froze for only part of the window: neither story fits cleanly.
  return 'unknown'
}

/**
 * Fold a live "how long since the worker's last beat" observation into the last recorded
 * gap. A reader that runs before the next 1s interval callback still learns the process was
 * suspended — without this, `getWorkerSuspendProbe` returns zeros on the resume that matters.
 */
export function resolveWorkerSuspendProbe(input: {
  nowMs: number
  lastBeatMs: number
  lastGapMs: number
  lastGapAtMs: number
  gapThresholdMs?: number
}): { lastGapMs: number, lastGapAtMs: number } {
  const threshold = input.gapThresholdMs ?? 5_000
  if (input.lastBeatMs <= 0) {
    return { lastGapMs: input.lastGapMs, lastGapAtMs: input.lastGapAtMs }
  }
  const liveGap = input.nowMs - input.lastBeatMs
  if (liveGap >= threshold && liveGap >= input.lastGapMs) {
    return { lastGapMs: liveGap, lastGapAtMs: input.nowMs }
  }
  return { lastGapMs: input.lastGapMs, lastGapAtMs: input.lastGapAtMs }
}
